'use client';

import { EyeOff, Camera, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import EyeOpen from '../../../assets/icons/Eye.svg?react';
import user from '../../../assets/icons/avatar.svg?react';
import Maya from '../../../assets/icons/Maya Icon.svg?react';
import GCASH from '../../../assets/icons/GCASH Icon.svg?react';
import { Link } from 'react-router-dom';
import { Button, Checkbox, Label, TextInput } from 'flowbite-react';
import { useState, useRef, useEffect } from 'react';
import { userApi } from '../../../api/user';
import { useAuth } from '../../../context/AuthContext';
import Swal from 'sweetalert2';

// Role definitions
const rolesData = [
  { id: 1, name: 'Admin' },
  { id: 2, name: 'Incorporator' },
  { id: 3, name: 'Sub Operator' },
  { id: 4, name: 'General Coordinator' },
  { id: 5, name: 'Coordinator' },
  { id: 6, name: 'Sub Coordinator' },
  { id: 7, name: 'Agent' },
  { id: 8, name: 'Sub Agent' },
  { id: 9, name: 'Player' },
];

export default function Profile({ userData }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState(userData?.user?.name);
  const [email, setEmail] = useState(userData?.user?.email);
  // Handle initial mobile number display (strip +63 or 63)
  let initialMobile = userData?.user?.mobileNumber || "";
  if (initialMobile.startsWith("+63")) {
    initialMobile = initialMobile.slice(3);
  } else if (initialMobile.startsWith("63")) {
    initialMobile = initialMobile.slice(2);
  }
  if (initialMobile.startsWith("09")) {
    initialMobile = initialMobile.slice(1);
  }
  const [phoneNumber, setPhoneNumber] = useState(initialMobile);
  const [password, setPassword] = useState(userData?.user?.password);
  const { tokens, user } = useAuth();

  // Role dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(userData?.user?.roleId || 9); // Default to Player (9) if no role
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get available roles based on the strict hierarchy rule:
  // Users can only assign either the role directly below their own or Player role
  const availableRoles = [];

  // Always include Player role (id: 9)
  const playerRole = rolesData.find((role) => role.id === 9);
  if (playerRole) {
    availableRoles.push(playerRole);
  }

  // Add the role that is exactly one level below current user's role
  const directSubordinateRole = rolesData.find(
    (role) => role.id === user.roleId + 1
  );
  if (directSubordinateRole) {
    availableRoles.push(directSubordinateRole);
  }

  // Include current role of the user being edited if it's already set and is eligible
  const currentUserRole = rolesData.find((role) => role.id === selectedRole);
  if (
    currentUserRole &&
    (currentUserRole.id === user.roleId + 1 || currentUserRole.id === 9) &&
    !availableRoles.some((role) => role.id === currentUserRole.id)
  ) {
    availableRoles.unshift(currentUserRole);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Always save as 639... (Philippines)
      let mobile = phoneNumber.replace(/\D/g, ""); // Remove non-digits
      if (mobile.startsWith("0")) {
        mobile = mobile.slice(1);
      }
      mobile = `63${mobile}`;
      const payload = {
        name,
        mobileNumber: mobile,
        password,
        roleId: selectedRole, // Add role ID to payload
        paymentsNumbers: [], // Add payment numbers logic if needed
      };

      const response = await userApi.updateUser(
        userData.user.id,
        tokens.access.token,
        payload
      );

      if (response.status === 200) {
        Swal.fire({
          title: 'Success',
          text: 'User updated successfully',
          icon: 'success',
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: response.data.message,
          icon: 'error',
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.message,
        icon: 'error',
      });
    }
  };

  return (
    <>
      {/* Content Section */}
      <div className='w-full mt-4'>
        {/* Login Form */}
        <form className='space-y-5 px-3.5' onSubmit={handleSubmit}>
          <div>
            <Label
              htmlFor='phoneNumber'
              className='block text-sm text-[#695C5C;] font-poppins ml-1 mb-2'
            >
              Full Name
            </Label>
            <TextInput
              id='fullName'
              type='text'
              placeholder='fullName'
              required
              className='w-full'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label
              htmlFor='phoneNumber'
              className='block text-sm text-[#695C5C;] font-poppins ml-1 mb-2'
            >
              Mobile Number{' '}
            </Label>
            <div className="flex gap-1">
              <span className="flex items-center px-2 rounded-md border border-gray-300 bg-gray-50 text-sm">+63</span>
              <TextInput
                id='phoneNumber'
                type='tel'
                placeholder='9661852101'
                required
                className='w-full'
                value={phoneNumber}
                onChange={(e) => {
                  // Only allow digits
                  let value = e.target.value.replace(/\D/g, "");
                  // If starts with 09, remove leading 0
                  if (value.startsWith("09")) {
                    value = value.slice(1);
                  }
                  // Enforce max 10 digits after cleaning
                  if (value.length > 10) {
                    value = value.slice(0, 10);
                  }
                  setPhoneNumber(value);
                }}
              />
            </div>
          </div>

          {/* Role selection dropdown */}
          {/* <div>
            <Label
              htmlFor="role"
              className="block text-sm text-[#695C5C;] font-poppins ml-1 mb-2"
            >
              User Role
            </Label>
            <div className="relative w-full" ref={dropdownRef}>
              <button
                type="button"
                id="roleDropdownButton"
                className="w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span>{rolesData.find(role => role.id === selectedRole)?.name || "Player"}</span>
                <span className="text-gray-500">
                  {dropdownOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </span>
              </button>
              {dropdownOpen && (
                <ul className="absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md">
                  {availableRoles.map((role, index) => (
                    <li
                      key={role.id}
                      onClick={() => {
                        setSelectedRole(role.id);
                        setDropdownOpen(false);
                      }}
                      className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                        index !== 0 ? "border-t border-[#E1E7EF]" : ""
                      }`}
                    >
                      {role.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div> */}

          <div className='relative'>
            <Label
              htmlFor='password'
              className='block text-sm text-[#695C5C;] font-poppins ml-1 mb-2'
            >
              Password
            </Label>
            <TextInput
              id='password'
              type={showPassword ? 'text' : 'password'}
              placeholder='Enter Your Password'
              className='w-full'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type='button'
              className='absolute right-3 top-1/2 '
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className='h-5 w-5 text-gray-500' />
              ) : (
                // <Eye className="h-5 w-5 text-gray-500" />

                <EyeOpen className='w-full h-full object-cover' />
              )}
            </button>
          </div>

          {/* <div className="flex items-center gap-1">
            <button
              className={"bg-blue-50 text-blue-600"}
            >
              <GCASH
                width={60}
                height={47}
                className="rounded-lg"
              />
            </button>
            <TextInput
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter Your Password"
              className="w-full"
            />
            <button variant="outline" size="icon">
            <Trash2 className="text-[#9EACC0] w-5 h-6"/>
            </button>
          </div>

          <div className="flex items-center justify-center gap-3 ">
            <span className="text-sm font-normal"> Add Payment Number</span>

            <GCASH width={60}
              height={47}
              className="object-cover"
            />

            <Maya
              width={60}
              height={47}
              className="object-cover"
            />
          </div> */}

          <Button
            type='submit'
            style={{ marginTop: '40px', marginBottom: '40px' }}
            className='w-full bg-[#0E64D1] hover:bg-blue-900 text-white text-base font-semibold rounded-md'
          >
            Save Changes
          </Button>
        </form>
      </div>
    </>
  );
}
